import React, { createContext, ReactNode, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetFileToken } from 'api/axios/hooks/useGetFileToken';
import { useCreateFile } from 'api/graphql/hooks/directory/useCreateFile';
import { useCreateFolder } from 'api/graphql/hooks/directory/useCreateFolder';
import { isSystemFile, normalizeRelativePath } from 'utils/file-system.utils';
import { FileWithPath, FolderPath, ParsedFileStructure } from '../types/file-structure.types';
import { UploadProgress, UploadsState } from '../types/upload-progress.types';
import { ENTRY_TYPE_FILE, ENTRY_TYPE_FOLDER } from 'constants/entryTypes';
import { DirectoryErrors, FileErrors } from '../constants/errors';

type FolderStructureUploadContextValue = {
    uploadsState: UploadsState;
    parseFileStructure: (items: DataTransferItemList) => Promise<ParsedFileStructure>;
    createFolderStructure: (
        filesWithPaths: FileWithPath[],
        explicitFolders: FolderPath[],
        parentDirectoryId: string,
        uploadId?: string,
        structureId?: string,
    ) => Promise<{ totalCreated: number; totalFailed: number; errors: any[]; aborted: boolean }>;
    resetProgress: (uploadId?: string) => void;
};

const FolderStructureUploadContext = createContext<FolderStructureUploadContextValue | undefined>(
    undefined,
);

const useFolderStructureUploadImpl = (): FolderStructureUploadContextValue => {
    const { t } = useTranslation();
    const [uploadsState, setUploadsState] = useState<UploadsState>({
        uploads: new Map(),
        activeUploadIds: [],
    });

    const [abortedUploads] = useState(() => new Set<string>());

    const { getToken } = useGetFileToken();
    const { createFile } = useCreateFile();
    const { createFolder } = useCreateFolder();

    const addUpload = (
        uploadId: string,
        initialProgress: Omit<UploadProgress, 'id' | 'structureId'>,
        structureId?: string,
    ) => {
        setUploadsState((prev) => {
            const newUploads = new Map(prev.uploads);
            newUploads.set(uploadId, { ...initialProgress, id: uploadId, structureId });
            return {
                uploads: newUploads,
                activeUploadIds: [...prev.activeUploadIds, uploadId],
            };
        });
    };

    const updateUpload = (uploadId: string, update: Partial<Omit<UploadProgress, 'id'>>) => {
        setUploadsState((prev) => {
            const newUploads = new Map(prev.uploads);
            const currentUpload = newUploads.get(uploadId);
            if (currentUpload) {
                newUploads.set(uploadId, { ...currentUpload, ...update });
            }
            return {
                ...prev,
                uploads: newUploads,
            };
        });
    };

    const completeUpload = (uploadId: string) => {
        setUploadsState((prev) => ({
            uploads: prev.uploads,
            activeUploadIds: prev.activeUploadIds.filter((id) => id !== uploadId),
        }));
    };

    const parseFileStructure = async (
        items: DataTransferItemList,
    ): Promise<ParsedFileStructure> => {
        const filesWithPaths: FileWithPath[] = [];
        const folderPaths: FolderPath[] = [];

        const processEntry = async (
            entry: FileSystemEntry,
            basePath = '',
            isRoot = false,
        ): Promise<void> => {
            if (entry.isFile) {
                const fileEntry = entry as FileSystemFileEntry;
                return new Promise((resolve, reject) => {
                    fileEntry.file((file) => {
                        if (isSystemFile(file.name)) {
                            resolve();
                            return;
                        }

                        const relativePath = isRoot ? '' : basePath;
                        const fullPath = basePath + file.name;
                        filesWithPaths.push({
                            file,
                            relativePath,
                            fullPath,
                        });
                        resolve();
                    }, reject);
                });
            } else if (entry.isDirectory) {
                const dirEntry = entry as FileSystemDirectoryEntry;
                const dirReader = dirEntry.createReader();

                return new Promise((resolve, reject) => {
                    const allEntries: FileSystemEntry[] = [];

                    const readEntries = () => {
                        dirReader.readEntries(async (entries) => {
                            if (entries.length === 0) {
                                if (!isRoot) {
                                    const folderPath = basePath + dirEntry.name;
                                    folderPaths.push({
                                        path: folderPath,
                                        isEmpty: allEntries.length === 0,
                                    });
                                }

                                resolve();
                                return;
                            }

                            allEntries.push(...entries);

                            const newBasePath = isRoot ? '' : basePath + dirEntry.name + '/';

                            const promises = entries.map((entry) =>
                                processEntry(entry, newBasePath, false),
                            );

                            try {
                                await Promise.all(promises);
                                readEntries();
                            } catch (error) {
                                reject(error);
                            }
                        }, reject);
                    };
                    readEntries();
                });
            }
        };

        const promises = Array.from(items).map((item) => {
            const entry = item.webkitGetAsEntry();

            if (entry) {
                return processEntry(entry, '', true);
            }
            return Promise.resolve();
        });

        await Promise.all(promises);

        if (filesWithPaths.length === 0 && folderPaths.length === 0) {
            for (let i = 0; i < items.length; i++) {
                const item = items[i];
                if (item.kind === 'file' && typeof item.getAsFile === 'function') {
                    const file = item.getAsFile();
                    if (file && !isSystemFile(file.name)) {
                        filesWithPaths.push({
                            file,
                            relativePath: '',
                            fullPath: file.name,
                        });
                    }
                }
            }
        }

        return { files: filesWithPaths, folders: folderPaths };
    };

    const getUniqueNameWithCopy = (name: string, existingError: string): string => {
        if (
            existingError.includes(DirectoryErrors.NAME_EXISTS) ||
            existingError.includes(FileErrors.NAME_EXISTS)
        ) {
            const copyMatch = name.match(/(.*?)(copy-(\d+))?$/);
            if (copyMatch) {
                const baseName = copyMatch[1].replace(/\s+$/, '');
                const currentNumber = copyMatch[3] ? parseInt(copyMatch[3]) : 0;
                return `${baseName} copy-${currentNumber + 1}`;
            }
            return `${name} copy-1`;
        }
        return name;
    };

    const createFolderStructure = async (
        filesWithPaths: FileWithPath[],
        explicitFolders: FolderPath[],
        parentDirectoryId: string,
        uploadId: string = crypto.randomUUID(),
        structureId?: string,
    ) => {
        const implicitFolderPaths = new Set<string>();
        filesWithPaths.forEach(({ relativePath }) => {
            if (relativePath) {
                const pathParts = relativePath.split('/').filter(Boolean);
                for (let i = 1; i <= pathParts.length; i++) {
                    const folderPath = pathParts.slice(0, i).join('/');
                    if (folderPath) {
                        implicitFolderPaths.add(folderPath);
                    }
                }
            }
        });

        const allFolderPaths = new Set<string>();

        implicitFolderPaths.forEach((path) => {
            allFolderPaths.add(path);
        });

        explicitFolders.forEach(({ path }) => {
            allFolderPaths.add(path);
        });

        const sortedFolderPaths = Array.from(allFolderPaths).sort();
        const totalItems = sortedFolderPaths.length + filesWithPaths.length;

        addUpload(
            uploadId,
            {
                totalItems,
                completedItems: 0,
                failedItems: 0,
                uploading: true,
                currentItem: '',
                errors: [],
                createdFolders: new Map([['', parentDirectoryId]]),
            },
            structureId,
        );

        let completed = 0;
        let failed = 0;
        const errors: Array<{
            path: string;
            error: string;
            type: typeof ENTRY_TYPE_FOLDER | typeof ENTRY_TYPE_FILE;
        }> = [];
        const createdFolders = new Map([['', parentDirectoryId]]);

        for (const folderPath of sortedFolderPaths) {
            if (abortedUploads.has(uploadId)) {
                return {
                    totalCreated: completed,
                    totalFailed: failed,
                    errors: [
                        ...errors,
                        {
                            path: t('general-mode-directory.upload-aborted-path'),
                            error: t('general-mode-directory.upload-aborted-error'),
                            type: ENTRY_TYPE_FOLDER,
                        },
                    ],
                    aborted: true,
                };
            }
            updateUpload(uploadId, {
                currentItem: t('general-mode-directory.creating-folder', {
                    folderName: folderPath,
                }),
                completedItems: completed,
            });

            try {
                const pathParts = folderPath.split('/');
                const folderName = pathParts[pathParts.length - 1];
                const parentPath = pathParts.slice(0, -1).join('/');
                const parentId = createdFolders.get(parentPath) || parentDirectoryId;

                let currentFolderName = folderName;
                let retryCount = 0;
                let error;
                let data;

                do {
                    const result = await createFolder({
                        name: currentFolderName,
                        parentId,
                    });
                    error = result.error;
                    data = result.data;

                    if (error) {
                        currentFolderName = getUniqueNameWithCopy(currentFolderName, String(error));
                        retryCount++;
                    }
                } while (error && retryCount < 10);

                if (error) {
                    throw new Error(String(error));
                }

                const folderId = data?.createDirectory?.directory?.id;
                if (folderId) {
                    createdFolders.set(folderPath, folderId);
                }

                completed++;
            } catch (error) {
                failed++;
                const errorMessage = error instanceof Error ? error.message : 'Unknown error';
                errors.push({ path: folderPath, error: errorMessage, type: ENTRY_TYPE_FOLDER });
            }

            await new Promise((resolve) => setTimeout(resolve, 100));
        }

        for (const { file, relativePath, fullPath } of filesWithPaths) {
            if (abortedUploads.has(uploadId)) {
                return {
                    totalCreated: completed,
                    totalFailed: failed,
                    errors: [
                        ...errors,
                        {
                            path: t('general-mode-directory.upload-aborted-path'),
                            error: t('general-mode-directory.upload-aborted-error'),
                            type: ENTRY_TYPE_FILE,
                        },
                    ],
                    aborted: true,
                };
            }
            updateUpload(uploadId, {
                currentItem: t('general-mode-directory.uploading-file', { fileName: file.name }),
                completedItems: completed,
                createdFolders,
            });

            try {
                const cleanRelativePath = normalizeRelativePath(relativePath);
                const directoryId = createdFolders.get(cleanRelativePath) || parentDirectoryId;

                let currentFile = file;
                let retryCount = 0;
                let error;

                do {
                    const fileUploadToken = await getToken(currentFile);
                    const result = await createFile({
                        directoryId,
                        fileUploadToken,
                    });
                    error = result.error;

                    if (error) {
                        const ext = currentFile.name.split('.').pop() || '';
                        const baseName = currentFile.name.slice(0, -(ext.length + 1));
                        const newName = getUniqueNameWithCopy(baseName, String(error));
                        currentFile = new File([currentFile], `${newName}.${ext}`, {
                            type: currentFile.type,
                        });
                        retryCount++;
                    }
                } while (error && retryCount < 10);

                if (error) {
                    throw new Error(String(error));
                }

                completed++;
            } catch (error) {
                failed++;
                const errorMessage = error instanceof Error ? error.message : 'Unknown error';
                errors.push({ path: fullPath, error: errorMessage, type: ENTRY_TYPE_FILE });
            }

            updateUpload(uploadId, {
                completedItems: completed,
                failedItems: failed,
                errors,
            });

            await new Promise((resolve) => setTimeout(resolve, 100));
        }

        updateUpload(uploadId, {
            uploading: false,
            currentItem: t('general-mode-directory.structure-finished'),
        });
        completeUpload(uploadId);

        return {
            totalCreated: completed,
            totalFailed: failed,
            errors,
            aborted: false,
        };
    };

    const resetProgress = (uploadId?: string) => {
        if (uploadId) {
            abortedUploads.add(uploadId);
            setUploadsState((prev) => {
                const upload = prev.uploads.get(uploadId);
                if (upload?.uploading) {
                    const newUploads = new Map(prev.uploads);
                    newUploads.set(uploadId, {
                        ...upload,
                        uploading: false,
                        currentItem: t('general-mode-directory.upload-aborted'),
                    });
                    return {
                        uploads: newUploads,
                        activeUploadIds: prev.activeUploadIds.filter((id) => id !== uploadId),
                    };
                }
                const newUploads = new Map(prev.uploads);
                newUploads.delete(uploadId);
                return {
                    uploads: newUploads,
                    activeUploadIds: prev.activeUploadIds.filter((id) => id !== uploadId),
                };
            });
        } else {
            setUploadsState({
                uploads: new Map(),
                activeUploadIds: [],
            });
        }
    };

    return {
        uploadsState,
        parseFileStructure,
        createFolderStructure,
        resetProgress,
    };
};

interface ProviderProps {
    children: ReactNode;
}

export const FolderStructureUploadProvider = ({ children }: ProviderProps) => {
    const value = useFolderStructureUploadImpl();

    return (
        <FolderStructureUploadContext.Provider value={value}>
            {children}
        </FolderStructureUploadContext.Provider>
    );
};

export const useBatchCreateFolderStructure = (): FolderStructureUploadContextValue => {
    const ctx = useContext(FolderStructureUploadContext);

    if (!ctx) {
        throw new Error(
            'useBatchCreateFolderStructure must be used within FolderStructureUploadProvider',
        );
    }

    return ctx;
};
